/**
 * Tracking Service — sends visitor tracking data to the backend API.
 */

import { clearVisits, VisitRecord } from '../utils/visitorTracker';

import axiosInstance from './axiosInstance';
import { tokenService } from './tokenService';

export interface ExploreSearchResultAnalytics {
  id: string;
  type: string;
  ranking_position: number;
  relevance_score?: number | null;
}

export type ExploreSearchEventType = 'search_completed' | 'search_click';

export interface ExploreSearchEventPayload {
  event_type: ExploreSearchEventType;
  search_id: string;
  visitor_id: string;
  query_text: string;
  timestamp: string;
  url: string;
  total_results_count?: number;
  top_results?: ExploreSearchResultAnalytics[];
  clicked_result_id?: string;
  clicked_position?: number;
  clicked_result_type?: string;
  metadata?: Record<string, string | number | boolean | null | undefined>;
}

export interface ProjectViewEventPayload {
  project_id: string;
  visitor_id: string;
  timestamp: string;
  url: string;
  referrer_url?: string | null;
  source?: string | null;
  metadata?: Record<string, string | number | boolean | null | undefined>;
}

interface ExploreSearchEventOptions {
  keepalive?: boolean;
}

const buildExploreSearchEventUrl = (): string => {
  const baseUrl = process.env.REACT_APP_API_URL || '';

  return `${baseUrl}/api/v1/tracking/explore-search-events`;
};

const sendExploreSearchEventWithFetch = (
  event: ExploreSearchEventPayload,
): Promise<void> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = tokenService.getAccessToken();

  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(buildExploreSearchEventUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(event),
    keepalive: true,
  }).then(() => undefined);
};

/**
 * Send accumulated visitor data and the real GA browser identity to the backend.
 * A false result keeps local acquisition data available for a later retry.
 */
// eslint-disable-next-line max-statements
export const sendVisitorData = async (
  visits: VisitRecord[],
  gaClientId?: string | null,
  isCurrent: () => boolean = () => true,
): Promise<boolean> => {
  if (!visits.length && !gaClientId) return true;

  try {
    // The API accepts at most 100 visits. Acknowledge each batch independently
    // so a failure, new visit or another tab cannot erase unsent acquisition data.
    for (let offset = 0; offset < Math.max(visits.length, 1); offset += 100) {
      if (!isCurrent()) return false;

      const batch = visits.slice(offset, offset + 100);
      const response = await axiosInstance.post('/api/v1/tracking/visitor-data', {
        visits: batch,
        ga_client_id: gaClientId || null,
      });

      clearVisits(batch);
      if (gaClientId && response.data.ga_client_id_stored === false) return false;
    }

    return true;
  } catch {
    // Keep the local copy so a later authenticated page load can retry it.
    return false;
  }
};

export const sendExploreSearchEvent = async (
  event: ExploreSearchEventPayload,
  options?: ExploreSearchEventOptions,
): Promise<void> => {
  try {
    if (options?.keepalive && typeof fetch !== 'undefined') {
      await sendExploreSearchEventWithFetch(event);

      return;
    }

    await axiosInstance.post('/api/v1/tracking/explore-search-events', event);
  } catch {
    // Search analytics should never interrupt Explore browsing.
  }
};

export const sendProjectViewEvent = async (
  event: ProjectViewEventPayload,
): Promise<void> => {
  try {
    await axiosInstance.post('/api/v1/tracking/project-view-events', event);
  } catch {
    // Project view analytics should never interrupt public project browsing.
  }
};
